import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Badge, Text, Select, TextField,
  DataTable, EmptyState, Spinner, Layout, BlockStack,
  InlineStack, InlineGrid, Box, Pagination,
} from '@shopify/polaris';
import { CashDollarIcon, ChartLineIcon, AlertTriangleIcon, DiscountIcon } from '@shopify/polaris-icons';
import { useProfit } from '../../hooks/useProfit';
import type { ProfitSnapshot } from '../../api/profit';
import { StatCard } from '../shared/StatCard';

function marginBadge(percent: number) {
  const tone = percent >= 30 ? 'success' : percent >= 10 ? 'attention' : 'critical';
  return <Badge tone={tone}>{`${percent}% margin`}</Badge>;
}

interface DetailProps {
  snapshot: ProfitSnapshot;
  onBack: () => void;
  onDelete: (id: number) => void;
}

function SnapshotDetail({ snapshot, onBack, onDelete }: DetailProps) {
  return (
    <Page fullWidth
      title={`Profit Snapshot: ${snapshot.period_start} – ${snapshot.period_end}`}
      backAction={{ content: 'All Snapshots', onAction: onBack }}
      secondaryActions={[{
        content: 'Delete',
        destructive: true,
        onAction: () => { onDelete(snapshot.id); onBack(); },
      }]}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <InlineGrid columns={{ xs: 1, sm: 4 }} gap="300">
              <StatCard label="Net Profit" value={`$${snapshot.net_profit.toLocaleString()}`} icon={CashDollarIcon} iconColor="green" />
              <StatCard label="Margin" value={`${snapshot.profit_margin_percent}%`} icon={ChartLineIcon} iconColor="purple" />
              <StatCard label="Refunds" value={`$${snapshot.total_refunds.toLocaleString()}`} icon={AlertTriangleIcon} iconColor="orange" />
              <StatCard label="Discounts" value={`$${snapshot.total_discounts.toLocaleString()}`} icon={DiscountIcon} iconColor="indigo" />
            </InlineGrid>

            {snapshot.ai_summary && (
              <Card>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">Summary</Text>
                  <Text as="p" variant="bodyMd">{snapshot.ai_summary}</Text>
                </BlockStack>
              </Card>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Breakdown</Text>
                <DataTable
                  columnContentTypes={['text', 'numeric']}
                  headings={['Line item', 'Amount']}
                  rows={[
                    ['Gross revenue', `$${snapshot.gross_revenue.toLocaleString()}`],
                    ['Discounts given', `-$${snapshot.total_discounts.toLocaleString()}`],
                    ['Refunds', `-$${snapshot.total_refunds.toLocaleString()}`],
                    ['Estimated payment processing fees', `-$${snapshot.est_payment_fees.toLocaleString()}`],
                    ['Ad spend', `-$${snapshot.ad_spend.toLocaleString()}`],
                    ['Net profit (estimate)', `$${snapshot.net_profit.toLocaleString()}`],
                  ]}
                />
                <Text as="p" tone="subdued" variant="bodySm">
                  Payment processing fees are estimated at 2.9% + $0.30/order — Shopify does not expose your actual
                  negotiated rate via the API. Ad spend is what you entered when generating this snapshot, not a live
                  ad-platform integration.
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">AI Recommendations</Text>
                <DataTable
                  columnContentTypes={['text', 'text', 'text']}
                  headings={['Title', 'Action', 'Expected Impact']}
                  rows={snapshot.ai_recommendations.map((r) => [r.title, r.action, r.expected_impact])}
                />
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

const PERIOD_OPTIONS = [
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 60 days', value: '60' },
  { label: 'Last 90 days', value: '90' },
];

export function ProfitPanel() {
  const {
    snapshots, activeSnapshot, loading, generating, error,
    total, page, lastPage, fetchSnapshots, generate, deleteSnapshot, setActiveSnapshot, setError,
  } = useProfit();

  const [days, setDays] = useState('30');
  const [adSpend, setAdSpend] = useState('0');

  useEffect(() => {
    fetchSnapshots(1);
  }, []);

  if (activeSnapshot) {
    return (
      <SnapshotDetail
        snapshot={activeSnapshot}
        onBack={() => setActiveSnapshot(null)}
        onDelete={deleteSnapshot}
      />
    );
  }

  const latest = snapshots[0];
  const handleGenerate = () => generate({ days: Number(days), ad_spend: Number(adSpend) || 0 });

  return (
    <Page fullWidth
      title="Profit & Margin Insights"
      subtitle="What you actually made after refunds, fees, and ad spend — not just revenue"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          {latest && (
            <InlineGrid columns={{ xs: 1, sm: 4 }} gap="300">
              <StatCard label="Net Profit (latest)" value={`$${latest.net_profit.toLocaleString()}`} icon={CashDollarIcon} iconColor="green" />
              <StatCard label="Margin" value={`${latest.profit_margin_percent}%`} icon={ChartLineIcon} iconColor="purple" />
              <StatCard label="Refunds" value={`$${latest.total_refunds.toLocaleString()}`} icon={AlertTriangleIcon} iconColor="orange" />
              <StatCard label="Discounts" value={`$${latest.total_discounts.toLocaleString()}`} icon={DiscountIcon} iconColor="indigo" />
            </InlineGrid>
          )}

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Generate New Snapshot</Text>
              <Text as="p" tone="subdued">
                Enter your ad spend for the period so it can be netted out of revenue — GrowthPilot doesn't pull this
                automatically from Facebook/Google Ads.
              </Text>
              <InlineStack gap="300" blockAlign="end">
                <Box minWidth="200px">
                  <Select label="Period" options={PERIOD_OPTIONS} value={days} onChange={setDays} />
                </Box>
                <Box minWidth="200px">
                  <TextField
                    label="Ad spend for this period ($)"
                    type="number"
                    value={adSpend}
                    onChange={setAdSpend}
                    autoComplete="off"
                    min={0}
                  />
                </Box>
                <Button
                  variant="primary"
                  loading={generating}
                  onClick={handleGenerate}
                >
                  Generate Snapshot
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Snapshots ({total})</Text>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : snapshots.length === 0 ? (
                <EmptyState
                  heading="No profit snapshots yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{ content: 'Generate First Snapshot', onAction: handleGenerate }}
                >
                  <p>Generate a snapshot to see net profit after refunds, estimated fees, and ad spend — not just revenue.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'text', 'text']}
                  headings={['Period', 'Net Profit', 'Margin', 'Actions']}
                  rows={snapshots.map((s) => [
                    <Button variant="plain" onClick={() => setActiveSnapshot(s)}>{`${s.period_start} – ${s.period_end}`}</Button>,
                    `$${s.net_profit.toLocaleString()}`,
                    marginBadge(s.profit_margin_percent),
                    <Button variant="plain" tone="critical" size="slim" onClick={() => deleteSnapshot(s.id)}>Delete</Button>,
                  ])}
                />
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchSnapshots(page - 1)}
                    hasNext={page < lastPage}
                    onNext={() => fetchSnapshots(page + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
